import { useParams, Link, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { ArrowLeft, Trash2, MapPin } from 'lucide-react'
import { useClients } from '../hooks/useClients'
import { useShares } from '../hooks/useShares'
import { useClientActivity } from '../hooks/useClientActivity'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import type { Client, Share } from '../types'

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = days / 30.44
  if (months < 12) return `${months.toFixed(1)}mo ago`
  const years = months / 12
  return `${years.toFixed(1)}y ago`
}

const formatDate = (dateStr: string) => {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  return `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()}`
}

export function ClientDetailPage() {
  const { email: emailParam } = useParams<{ email: string }>()
  const email = decodeURIComponent(emailParam || '')
  const navigate = useNavigate()
  const { getClient, updateClient, deleteClient } = useClients()
  const { shares } = useShares({ target: email })
  const { activity, loading: activityLoading } = useClientActivity(email)

  const [client, setClient] = useState<Client | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [actionError, setActionError] = useState('')

  // Draft state
  const [draftName, setDraftName] = useState('')
  const [draftRole, setDraftRole] = useState('client')
  const [draftPin, setDraftPin] = useState('')

  // Load client
  useEffect(() => {
    if (!email) return
    setLoading(true)
    getClient(email)
      .then(c => {
        setClient(c)
        setDraftName(c.name)
        setDraftRole(c.role || 'client')
        setDraftPin(c.pin || '')
      })
      .catch(err => setError(err.message || 'Failed to load client'))
      .finally(() => setLoading(false))
  }, [email])

  const isDirty = client != null && (
    draftName !== client.name ||
    draftRole !== (client.role || 'client') ||
    (draftPin !== '' && draftPin !== (client.pin || ''))
  )

  async function handleSave() {
    if (!client || !isDirty) return
    setSaving(true)
    setActionError('')
    try {
      const updates: Record<string, unknown> = {
        name: draftName,
        role: draftRole,
      }
      if (draftPin && draftPin !== (client.pin || '')) {
        updates.pin = draftPin
      }
      await updateClient(email, updates as any)
      // Refresh
      const c = await getClient(email)
      setClient(c)
      setDraftName(c.name)
      setDraftRole(c.role || 'client')
      setDraftPin(c.pin || '')
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    if (!confirm(`Delete client ${email}? This cannot be undone.`)) return
    try {
      await deleteClient(email)
      navigate('/users')
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete')
    }
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <Skeleton className="h-8 w-1/4 mb-4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    )
  }

  if (error || !client) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error || 'Client not found'}</AlertDescription>
        </Alert>
      </div>
    )
  }

  // Parse change history from meta
  const clientMeta = (() => {
    // We need raw meta — re-fetch via the API would be better but let's use what we have
    return null // changes come from the client object
  })()

  const totalVisits = activity?.seeds.reduce((sum, s) => sum + s.visits, 0) || 0
  const lastSeen = activity?.seeds[0]?.lastVisit

  // Client shares — match against any workspace member (email or domain).
  const userDomain = email.includes('@') ? email.split('@')[1] : ''
  const clientShares = shares.filter(s =>
    s.workspaces.some(w =>
      w.members.some(m =>
        (m.kind === 'email' && m.recipient === email)
        || (m.kind === 'domain' && m.recipient === userDomain)
      )
    )
  )

  return (
    <div className="mx-auto max-w-5xl p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Button variant="ghost" size="sm" asChild className="mb-2">
            <Link to="/users">
              <ArrowLeft className="h-4 w-4" />
              Back to Users
            </Link>
          </Button>
          <h1 className="text-2xl font-bold text-foreground">{draftName || email}</h1>
          <p className="text-sm text-muted-foreground">{email}</p>
          <Badge variant={draftRole === 'admin' ? 'default' : 'outline'} className={cn('mt-1', draftRole === 'admin' && 'bg-amber-100 text-amber-800 border-0')}>
            {draftRole}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={handleDelete}>
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/users')}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={!isDirty || saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {actionError && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Profile Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Profile</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={draftName} onChange={e => setDraftName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={email} disabled className="text-muted-foreground" />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={draftRole} onValueChange={setDraftRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>PIN</Label>
              <Input
                value={draftPin}
                onChange={e => setDraftPin(e.target.value)}
                placeholder="Leave empty to keep current"
              />
            </div>
            <div className="flex gap-4 text-xs text-muted-foreground pt-2">
              <span>Created: {formatDate(client.createdAt)}</span>
              <span>Updated: {formatDate(client.updatedAt)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Shares Card */}
        <Card>
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium">Shares</CardTitle>
            <Button size="sm" variant="outline" asChild>
              <Link to={`/shares/new?email=${encodeURIComponent(email)}`}>Create Share</Link>
            </Button>
          </CardHeader>
          <CardContent>
            {clientShares.length === 0 ? (
              <p className="text-sm text-muted-foreground">No shares targeting this client.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Name</TableHead>
                    <TableHead className="text-xs">Seed</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs">Expires</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clientShares.map(s => (
                    <TableRow key={s.id} className="cursor-pointer" onClick={() => navigate(`/shares/${s.id}`)}>
                      <TableCell className="text-sm">
                        <Link to={`/shares/${s.id}`} className="text-foreground hover:text-seed-green-600">
                          {s.name || `#${s.id}`}
                        </Link>
                      </TableCell>
                      <TableCell className="text-sm">
                        <Link to={`/seeds/${s.seed}`} className="text-seed-green-600 hover:text-seed-green-500 font-medium">
                          {s.seed}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <span className={cn(
                          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
                          s.expired ? 'bg-gray-100 text-gray-700'
                            : s.status === 'draft' ? 'bg-gray-100 text-gray-700'
                            : 'bg-emerald-100 text-emerald-700',
                        )}>
                          <span className={cn('h-1.5 w-1.5 rounded-full',
                            s.expired ? 'bg-gray-500' : s.status === 'draft' ? 'bg-gray-500' : 'bg-emerald-500'
                          )} />
                          {s.expired ? 'Expired' : s.status === 'draft' ? 'Draft' : 'Published'}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {s.expiresAt ? timeAgo(s.expiresAt) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Activity Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {activityLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
              </div>
            ) : !activity || activity.seeds.length === 0 ? (
              <p className="text-sm text-muted-foreground">No activity recorded.</p>
            ) : (
              <>
                <div className="flex gap-4 text-xs text-muted-foreground mb-3">
                  <span>Total visits: {totalVisits}</span>
                  {lastSeen && <span>Last seen: {timeAgo(lastSeen)}</span>}
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Seed</TableHead>
                      <TableHead className="text-xs text-right">Visits</TableHead>
                      <TableHead className="text-xs">Last Visit</TableHead>
                      <TableHead className="text-xs">Country</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activity.seeds.map(s => (
                      <TableRow key={s.seed}>
                        <TableCell>
                          <Link to={`/seeds/${s.seed}`} className="text-sm font-medium text-foreground hover:text-primary">
                            {s.seed}
                          </Link>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground text-right">{s.visits}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{timeAgo(s.lastVisit)}</TableCell>
                        <TableCell>
                          {s.countries.length > 0 ? (
                            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                              <MapPin className="w-3 h-3 shrink-0" />
                              {s.countries.slice(0, 3).join(', ')}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
